import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, Building2, PenTool, Play, Radio } from 'lucide-react';
import VisitorNav from './VisitorNav';
import VisitorTopMenuBar from './VisitorTopMenuBar';
import VisitorFooter from './VisitorFooter';
import VisitorMobileBottomNav from './VisitorMobileBottomNav';
import AskAudiaModal from './AskAudiaModal';
import RegistrationModal from './RegistrationModal';
import FounderWelcomeModal from './FounderWelcomeModal';
import ForAuthorsModal from './ForAuthorsModal';
import ForPublishersModal from './ForPublishersModal';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { Seo, PAGE_SEO } from './Seo';

const NewLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAskAudiaOpen, setIsAskAudiaOpen] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showFounderModal, setShowFounderModal] = useState(false);
  const [showAuthorsModal, setShowAuthorsModal] = useState(false);
  const [showPublishersModal, setShowPublishersModal] = useState(false);
  const [titleCount, setTitleCount] = useState(null);
  const [radioPlaylist, setRadioPlaylist] = useState([]);
  const [radioIndex, setRadioIndex] = useState(0);
  const [radioTransition, setRadioTransition] = useState(false);
  const [radioNextTrack, setRadioNextTrack] = useState(null);

  useEffect(() => { setIsVisible(true); }, []);

  // Fetch the dynamic title count on mount
  useEffect(() => {
    const fetchTitleCount = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/library/public/stats`);
        if (response.ok) {
          const data = await response.json();
          setTitleCount(data.total_titles);
        }
      } catch (error) {
        console.error('Failed to fetch library stats:', error);
      }
    };
    fetchTitleCount();
  }, []);

  const handleSignIn = () => navigate('/login');
  const handleGetStarted = () => setShowRegistrationModal(true);
  const openAskAudia = () => setIsAskAudiaOpen(true);
  const closeAskAudia = () => setIsAskAudiaOpen(false);

  const handleListenFree = async () => {
    try {
      const { apiFetch } = await import('../lib/apiFetch');
      const result = await apiFetch(`${backendUrl}/api/radio/playlist`);
      if (!result.ok) throw new Error('Playlist unavailable');
      const data = result.json;

      if (data.status === 'insufficient_content' || !data.playlist?.length) {
        alert('No samples available right now — check back soon.');
        return;
      }

      const playlist = data.playlist;
      setRadioPlaylist(playlist);
      setRadioIndex(0);
      launchRadioTrack(playlist, 0);
    } catch (error) {
      console.error('Error starting radio:', error);
      alert('Unable to start radio — please try again shortly.');
    }
  };

  const launchRadioTrack = async (playlist, index) => {
    const track = playlist[index];

    try {
      const { apiFetch } = await import('../lib/apiFetch');

      const sessionResult = await apiFetch(
        `${backendUrl}/api/manuscripts/${track.id}/sample/guest`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
      );

      if (!sessionResult.ok) {
        const nextIndex = (index + 1) % playlist.length;
        setRadioIndex(nextIndex);
        launchRadioTrack(playlist, nextIndex);
        return;
      }

      navigate(`/player/${sessionResult.json.session_id}`, {
        state: {
          returnTo: '/',
          autoplay: true,
          guestSample: true,
          isGuest: true,
          radioMode: true
        }
      });
    } catch (error) {
      const nextIndex = (index + 1) % playlist.length;
      setRadioIndex(nextIndex);
      launchRadioTrack(playlist, nextIndex);
    }
  };

  // Detect return from radio player when visitor clicked "Yes, keep listening"
  useEffect(() => {
    if (location.state?.radioAdvance && radioPlaylist.length > 0) {
      window.history.replaceState({}, '');
      const nextIndex = (radioIndex + 1) % radioPlaylist.length;
      setRadioIndex(nextIndex);
      const nextTrack = radioPlaylist[nextIndex];
      setRadioNextTrack(nextTrack);
      setRadioTransition(true);
      setTimeout(() => {
        setRadioTransition(false);
        setRadioNextTrack(null);
        launchRadioTrack(radioPlaylist, nextIndex);
      }, 2500);
    }
  }, [location.state]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white pb-20 md:pb-0 overflow-x-hidden max-w-full">
      <Seo {...PAGE_SEO.home} />
      {/* Radio Transition Card — shown between tracks */}
      {radioTransition && radioNextTrack && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#1B3A6B]" data-testid="radio-transition-card">
          <p className="text-[#00A896] text-sm font-semibold uppercase tracking-widest mb-6">
            Up Next
          </p>
          {radioNextTrack.cover_url && (
            <img
              src={`${backendUrl}${radioNextTrack.cover_url}`}
              alt={radioNextTrack.title}
              className="w-40 h-56 object-cover rounded-lg shadow-2xl mb-6"
            />
          )}
          <p className="text-white text-2xl font-bold text-center px-8 mb-2">
            {radioNextTrack.title}
          </p>
          <p className="text-[#A8C4E0] text-base text-center px-8">
            {radioNextTrack.author}
          </p>
          <p className="text-[#A8C4E0] text-sm mt-8 opacity-60">
            Starting in a moment...
          </p>
        </div>
      )}
      <VisitorNav />
      <VisitorTopMenuBar />
      
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-8 sm:py-12 max-w-full overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'} overflow-hidden`}>
              <div className="mb-6 sm:mb-8 flex justify-center lg:justify-start">
                <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-32 sm:h-48 lg:h-60 w-auto drop-shadow-2xl" />
              </div>
              <h1 className="text-2xl sm:text-5xl lg:text-6xl font-bold mb-2 sm:mb-3 leading-tight text-white text-center lg:text-left break-words">Read. Listen. Ask.</h1>
              <p className="text-base sm:text-xl lg:text-2xl text-gray-300 mb-4 sm:mb-6 text-center lg:text-left break-words italic">The audiobook experience that talks back.</p>
              
              {/* Listen Free Now - Primary CTA directly below headline */}
              <div className="mb-4 sm:mb-6 flex justify-center lg:justify-start">
                <button
                  onClick={handleListenFree}
                  data-testid="radio-hero-cta-btn"
                  className="relative px-8 sm:px-12 py-5 sm:py-7 text-white font-semibold rounded-2xl whitespace-normal text-center bg-gradient-to-b from-purple-500 via-purple-600 to-indigo-700 border-t border-purple-400/50 shadow-[0_6px_0_0_#312e81,0_8px_20px_rgba(88,28,135,0.5)] hover:shadow-[0_4px_0_0_#312e81,0_6px_16px_rgba(88,28,135,0.5)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#312e81] active:translate-y-[5px] transition-all duration-100 cursor-pointer"
                >
                  <span className="flex flex-col items-center gap-1.5">
                    <span className="flex items-center gap-2 text-base sm:text-xl">
                      <Radio size={22} />
                      Try NewAudia Radio
                      <Radio size={22} />
                    </span>
                    <span className="text-lg sm:text-2xl font-bold tracking-wide">LISTEN FREE NOW</span>
                  </span>
                </button>
              </div>
              <p className="text-center lg:text-left text-xs sm:text-sm text-slate-400 mt-2">
                Instant access. No account needed. No sign-up required.
              </p>
              <p className="text-center lg:text-left text-sm sm:text-base text-white font-semibold mt-4">
                NewAudia is something new entirely.
              </p>

              <p className="text-sm sm:text-lg text-gray-300 mb-3 sm:mb-4 leading-relaxed max-w-2xl text-center break-words">Not audiobooks. Not eBooks.<br /><strong className="text-white">Interactive NFD-SmartBooks<sup className="text-xs">™</sup> that respond, explain, and engage — while you listen.</strong></p>
              <p className="text-xs sm:text-base text-gray-400 mb-4 sm:mb-5 text-center lg:text-left break-words">You don't just hear a story.<br />You explore it, question it, and understand it — without breaking immersion.</p>
              
              {/* Convert Your Book CTA - Above the fold */}
              <div className="mb-2 sm:mb-3 flex justify-center">
                <button 
                  onClick={() => setShowAuthorsModal(true)} 
                  className="relative bg-gradient-to-b from-amber-400 via-amber-500 to-orange-600 border-t border-amber-300/50 text-white px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-bold rounded-2xl shadow-[0_6px_0_0_#92400e,0_8px_20px_rgba(146,64,14,0.4)] hover:shadow-[0_4px_0_0_#92400e,0_6px_16px_rgba(146,64,14,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#92400e] active:translate-y-[5px] transition-all duration-100 flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer"
                  data-testid="convert-book-cta-btn"
                >
                  <PenTool className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="break-words">CONVERT YOUR BOOK TO AUDIO — TODAY!</span>
                </button>
              </div>
              
              {/* Publish for Clients CTA - Below Convert Your Book */}
              <div className="mb-4 sm:mb-6 flex justify-center">
                <button 
                  onClick={() => setShowPublishersModal(true)} 
                  className="relative bg-gradient-to-b from-purple-400 via-purple-500 to-indigo-600 border-t border-purple-300/50 text-white px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-bold rounded-2xl shadow-[0_6px_0_0_#312e81,0_8px_20px_rgba(88,28,135,0.4)] hover:shadow-[0_4px_0_0_#312e81,0_6px_16px_rgba(88,28,135,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#312e81] active:translate-y-[5px] transition-all duration-100 flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer"
                  data-testid="publish-book-cta-btn"
                >
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="break-words">PUBLISH A BOOK FOR YOUR CLIENTS</span>
                </button>
              </div>
              
              {/* Library Proof Line */}
              <div className="mb-6 sm:mb-8 text-center lg:text-left">
                <p className="text-sm sm:text-lg text-white font-semibold mb-1 break-words">
                  <span className="mr-2">🎧</span>Explore the NewAudia Library
                </p>
                <p className="text-xs sm:text-base text-purple-300 font-medium mb-1 break-words">{titleCount !== null ? `${titleCount}+ interactive titles available now` : 'Interactive titles available now'}</p>
                <p className="text-xs text-slate-400 italic break-words">Independent voices. Real conversations. More added regularly.</p>
              </div>
              
              <div className="flex flex-col gap-3 mb-6">
                <button onClick={() => navigate('/public/library')} className="relative bg-gradient-to-b from-blue-500 via-blue-600 to-cyan-700 border-t border-blue-400/50 text-white px-4 sm:px-8 py-4 sm:py-6 text-sm sm:text-lg font-semibold rounded-2xl shadow-[0_6px_0_0_#164e63,0_8px_20px_rgba(22,78,99,0.4)] hover:shadow-[0_4px_0_0_#164e63,0_6px_16px_rgba(22,78,99,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#164e63] active:translate-y-[5px] transition-all duration-100 flex items-center justify-center gap-2 w-full whitespace-normal text-center cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/><path d="M8 7h6"/><path d="M8 11h8"/></svg>
                  <span className="break-words">Browse the NewAudia Library</span>
                </button>
                <button onClick={handleGetStarted} className="relative bg-gradient-to-b from-amber-300 via-amber-400 to-yellow-500 border-t border-amber-200/50 text-black px-4 sm:px-6 py-3 sm:py-5 text-xs sm:text-base font-semibold rounded-2xl w-full whitespace-normal text-center shadow-[0_6px_0_0_#a16207,0_8px_20px_rgba(161,98,7,0.4)] hover:shadow-[0_4px_0_0_#a16207,0_6px_16px_rgba(161,98,7,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#a16207] active:translate-y-[5px] transition-all duration-100 cursor-pointer" data-testid="landing-get-credits-btn">
                  <span className="break-words">Start Now With 10 FREE Credits →</span>
                </button>
              </div>
              <p className="text-xs text-slate-400 mb-4 text-center lg:text-left break-words">
                Sign up free and start exploring with Audia, your AI reading companion.
              </p>
            </div>
            <div className={`transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
              <div className="flex justify-center mb-8">
                <div className="relative w-full max-w-xs lg:max-w-sm mx-auto">
                  <div className="relative rounded-[3rem] bg-gradient-to-br from-slate-800 to-slate-900 p-3 shadow-2xl transform hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={handleListenFree}>
                    <div className="relative rounded-[2.5rem] overflow-hidden bg-black p-2">
                      <img src="https://customer-assets.emergentagent.com/job_book-portal-4/artifacts/7u7tcftx_image.png" alt="NewAudia Player Interface" className="w-full h-auto rounded-[2rem] shadow-inner" />
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-3xl z-10"></div>
                    </div>
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-600 rounded-full"></div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-4">
                <button
                  onClick={handleListenFree}
                  data-testid="radio-play-now-btn"
                  className="relative flex items-center gap-2 bg-gradient-to-b from-[#00C4A7] via-[#00A896] to-[#008f7f] border-t border-[#00D4B5]/50 text-white font-semibold px-6 py-3 rounded-2xl shadow-[0_6px_0_0_#065f46,0_8px_20px_rgba(6,95,70,0.4)] hover:shadow-[0_4px_0_0_#065f46,0_6px_16px_rgba(6,95,70,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#065f46] active:translate-y-[5px] transition-all duration-100 cursor-pointer"
                >
                  <Radio size={18} />
                  Play Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEET AUDIA */}
      <section className="py-10 sm:py-16 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="bg-gradient-to-br from-purple-900/30 to-slate-900/50 border border-purple-500/30 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-center">
              <div className="overflow-hidden">
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <img src="/audia-avatar.jpg" alt="Audia" className="w-14 h-14 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-purple-500/50 shadow-lg flex-shrink-0" />
                  <div><h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Meet Audia.</h2><p className="text-sm sm:text-base text-purple-400">Your guide inside NewAudia<sup className="text-xs">™</sup></p></div>
                </div>
                <p className="text-sm sm:text-lg text-gray-300 mb-4 sm:mb-6 leading-relaxed break-words">Audia is designed to answer questions, explain concepts, and help you understand what's possible here.</p>
                <ul className="text-xs sm:text-base text-gray-400 space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  <li className="flex items-start gap-2 sm:gap-3"><MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0 mt-0.5" /><span className="break-words">"Is this an audiobook?"</span></li>
                  <li className="flex items-start gap-2 sm:gap-3"><MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0 mt-0.5" /><span className="break-words">"Do I have to buy the whole book?"</span></li>
                  <li className="flex items-start gap-2 sm:gap-3"><MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0 mt-0.5" /><span className="break-words">"What makes this different from Audible?"</span></li>
                  <li className="flex items-start gap-2 sm:gap-3"><MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0 mt-0.5" /><span className="break-words">"Can I try it before signing up?"</span></li>
                </ul>
                <p className="text-xs sm:text-base text-gray-300 mb-4 sm:mb-6 break-words">Audia answers in plain language.<br /><strong className="text-white">No account required.</strong></p>
                <button onClick={openAskAudia} className="relative bg-gradient-to-b from-purple-400 via-purple-500 to-pink-600 border-t border-purple-300/50 text-white px-4 sm:px-8 py-3 sm:py-5 text-sm sm:text-lg font-semibold rounded-2xl shadow-[0_6px_0_0_#831843,0_8px_20px_rgba(131,24,67,0.4)] hover:shadow-[0_4px_0_0_#831843,0_6px_16px_rgba(131,24,67,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#831843] active:translate-y-[5px] transition-all duration-100 w-full sm:w-auto cursor-pointer">💬 Ask Audia Now</button>
              </div>
              <div className="flex justify-center lg:justify-end mt-4 lg:mt-0">
                <div className="w-[60%] sm:w-[45%] rounded-2xl overflow-hidden border border-slate-700/50 shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_ae72f522-5b61-4e62-a6e7-aff9a74f638c/artifacts/2fssv01u_Ask_Audia_Player_Image.jpg" alt="Ask Audia Interface" className="w-full h-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE BREAK 1 */}
      <section className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_e363e369-275b-4574-bd33-a3cd56da553d/artifacts/e6h2rwu7_Image_7.jpg" alt="Feel the story" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/30"></div>
        <div className="absolute inset-0 flex items-center justify-center"><h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center px-4 drop-shadow-lg">Feel the story.</h3></div>
      </section>

      {/* THE SHIFT */}
      <section className="py-10 sm:py-16 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 text-white break-words">Books Have Been One-Way for 2,000 Years.</h2>
            <h3 className="text-base sm:text-2xl lg:text-3xl font-bold mb-6 sm:mb-8 break-words"><span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Today, Reading Becomes a Conversation.</span></h3>
            <p className="text-xs sm:text-lg text-gray-300 mb-3 sm:mb-4 leading-relaxed break-words">Every story leaves questions behind.<br />Why did they hesitate?<br />What did that moment really mean?<br />What was the author trying to say?</p>
            <p className="text-xs sm:text-lg text-gray-300 mb-6 sm:mb-8 leading-relaxed break-words">Until now, those answers lived outside the book.<br /><strong className="text-white">NewAudia<sup className="text-xs">™</sup> brings them inside.</strong></p>
            <button onClick={openAskAudia} className="relative bg-gradient-to-b from-slate-700 via-slate-800 to-slate-900 border-t border-purple-400/40 text-purple-400 px-4 sm:px-6 py-3 sm:py-4 rounded-2xl text-xs sm:text-base w-full sm:w-auto shadow-[0_6px_0_0_#1e1b4b,0_8px_20px_rgba(30,27,75,0.4)] hover:shadow-[0_4px_0_0_#1e1b4b,0_6px_16px_rgba(30,27,75,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#1e1b4b] active:translate-y-[5px] transition-all duration-100 font-semibold cursor-pointer">💬 Ask Audia what makes this different</button>
          </div>
        </div>
      </section>

      {/* IMAGE BREAK 2 */}
      <section className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_e363e369-275b-4574-bd33-a3cd56da553d/artifacts/4w6g3005_Image_2.png" alt="Stay inside the moment" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/30"></div>
        <div className="absolute inset-0 flex items-center justify-center"><h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center px-4 drop-shadow-lg">Stay inside the moment.</h3></div>
      </section>

      {/* CATEGORY */}
      <section className="py-10 sm:py-16 bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 text-white break-words">This Is Not an Audiobook Upgrade.</h2>
            <h3 className="text-base sm:text-2xl lg:text-3xl font-bold mb-6 sm:mb-8 break-words"><span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">It's a New Category of Reading.</span></h3>
            <ul className="text-xs sm:text-lg text-gray-300 space-y-3 sm:space-y-4 mb-6 sm:mb-8 max-w-xl mx-auto text-left px-4 sm:px-0">
              <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">Ask questions while the story plays</span></li>
              <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">Explore characters, motives, and themes in real time</span></li>
              <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">Go deeper without pausing or leaving the moment</span></li>
            </ul>
            <p className="text-sm sm:text-xl text-gray-300 break-words">You're not consuming content.<br /><strong className="text-white text-base sm:text-2xl">You're participating in it.</strong></p>
          </div>
        </div>
      </section>

      {/* IMAGE BREAK 3 */}
      <section className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_ae72f522-5b61-4e62-a6e7-aff9a74f638c/artifacts/ibbp5e7l_Image_3.jpg" alt="Engage on your terms" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/30"></div>
        <div className="absolute inset-0 flex items-center justify-center"><h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center px-4 drop-shadow-lg">Engage on your terms.</h3></div>
      </section>

      {/* ACCESS */}
      <section className="py-10 sm:py-16 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 text-white break-words">Explore Without Commitment</h2>
            <p className="text-xs sm:text-lg text-gray-300 mb-3 sm:mb-4 leading-relaxed break-words">We don't sell books.<br /><strong className="text-white">We provide access to experiences inside books.</strong></p>
            <ul className="text-xs sm:text-lg text-gray-300 space-y-2 sm:space-y-3 mb-6 sm:mb-8 max-w-lg mx-auto text-left px-4 sm:px-0">
              <li className="flex items-start gap-2 sm:gap-3"><span className="text-emerald-400 mt-1 flex-shrink-0">✓</span><span className="break-words">No obligation to buy the whole story</span></li>
              <li className="flex items-start gap-2 sm:gap-3"><span className="text-emerald-400 mt-1 flex-shrink-0">✓</span><span className="break-words">No pressure to finish what doesn't resonate</span></li>
              <li className="flex items-start gap-2 sm:gap-3"><span className="text-emerald-400 mt-1 flex-shrink-0">✓</span><span className="break-words">No barriers to curiosity</span></li>
            </ul>
            <p className="text-sm sm:text-xl text-gray-300 mb-6 sm:mb-8 break-words">Jump in. Explore what matters. Leave wanting more.</p>
            <button onClick={openAskAudia} className="relative bg-gradient-to-b from-slate-700 via-slate-800 to-slate-900 border-t border-emerald-400/40 text-emerald-400 px-4 sm:px-6 py-3 sm:py-4 rounded-2xl text-xs sm:text-base w-full sm:w-auto shadow-[0_6px_0_0_#064e3b,0_8px_20px_rgba(6,78,59,0.4)] hover:shadow-[0_4px_0_0_#064e3b,0_6px_16px_rgba(6,78,59,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#064e3b] active:translate-y-[5px] transition-all duration-100 font-semibold cursor-pointer">💬 Ask Audia how access works</button>
          </div>
        </div>
      </section>

      {/* AUTHORS & PUBLISHERS */}
      <section id="creators-section" className="py-10 sm:py-16 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="text-center max-w-4xl mx-auto mb-6 sm:mb-12">
            <h2 className="text-xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 text-white break-words">Built for Storytellers and Story Stewards</h2>
            <p className="text-xs sm:text-base lg:text-lg text-gray-300 leading-relaxed break-words">NewAudia<sup className="text-xs">™</sup> supports both independent authors and publishing organizations — each with tools, economics, and opportunities designed for how they actually work.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto">
            {/* Author Card */}
            <div className="bg-gradient-to-br from-blue-900/30 to-slate-900/50 border border-blue-500/30 rounded-2xl sm:rounded-3xl p-4 sm:p-8 overflow-hidden">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-500/20 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"><PenTool className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400" /></div>
                <h3 className="text-lg sm:text-2xl font-bold text-white">For Authors</h3>
              </div>
              <p className="text-xs sm:text-base text-gray-300 mb-4 sm:mb-6 leading-relaxed break-words">Turn your book into a fully functional audio experience — without the prohibitive cost of traditional audiobook production.</p>
              <ul className="text-xs sm:text-base text-gray-400 space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-blue-400 mt-1 flex-shrink-0">•</span><span className="break-words">Your first manuscript upload is free</span></li>
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-blue-400 mt-1 flex-shrink-0">•</span><span className="break-words">Additional uploads: upload fee per manuscript</span></li>
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-blue-400 mt-1 flex-shrink-0">•</span><span className="break-words">Earn commissions based on reader engagement</span></li>
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-blue-400 mt-1 flex-shrink-0">•</span><span className="break-words">No subscriptions. No royalties. No inventory.</span></li>
              </ul>
              <button onClick={() => setShowAuthorsModal(true)} className="relative bg-gradient-to-b from-blue-500 via-blue-600 to-blue-700 border-t border-blue-400/50 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-2xl font-semibold text-xs sm:text-base w-full sm:w-auto shadow-[0_6px_0_0_#1e3a8a,0_8px_20px_rgba(30,58,138,0.4)] hover:shadow-[0_4px_0_0_#1e3a8a,0_6px_16px_rgba(30,58,138,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#1e3a8a] active:translate-y-[5px] transition-all duration-100 cursor-pointer" data-testid="authors-learn-more-btn">Learn More</button>
            </div>
            {/* Publisher Card */}
            <div className="bg-gradient-to-br from-purple-900/30 to-slate-900/50 border border-purple-500/30 rounded-2xl sm:rounded-3xl p-4 sm:p-8 overflow-hidden">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-purple-500/20 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"><Building2 className="w-5 h-5 sm:w-7 sm:h-7 text-purple-400" /></div>
                <h3 className="text-lg sm:text-2xl font-bold text-white">For Publishers</h3>
              </div>
              <p className="text-xs sm:text-base text-gray-300 mb-4 sm:mb-6 leading-relaxed break-words">Extend the value of your catalog with interactive audio — without reallocating production budgets or disrupting workflows.</p>
              <ul className="text-xs sm:text-base text-gray-400 space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">First manuscript upload free per author account</span></li>
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">Scalable audio across backlist and frontlist titles</span></li>
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">Engagement-based commissions, not retail royalties</span></li>
                <li className="flex items-start gap-2 sm:gap-3"><span className="text-purple-400 mt-1 flex-shrink-0">•</span><span className="break-words">No subscriptions required</span></li>
              </ul>
              <button onClick={() => setShowPublishersModal(true)} className="relative bg-gradient-to-b from-purple-500 via-purple-600 to-purple-700 border-t border-purple-400/50 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-2xl font-semibold text-xs sm:text-base w-full sm:w-auto shadow-[0_6px_0_0_#581c87,0_8px_20px_rgba(88,28,135,0.4)] hover:shadow-[0_4px_0_0_#581c87,0_6px_16px_rgba(88,28,135,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#581c87] active:translate-y-[5px] transition-all duration-100 cursor-pointer" data-testid="publishers-learn-more-btn">Learn More</button>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE BREAK 4 - Before Final CTA */}
      <section className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_ae72f522-5b61-4e62-a6e7-aff9a74f638c/artifacts/tlx2fdq6_Image_1.png" alt="The future of reading" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/30"></div>
      </section>

      {/* UWYG ECOSYSTEM SECTION */}
      <section className="py-12 sm:py-16 bg-slate-950 border-t border-slate-800/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-lg sm:text-2xl lg:text-3xl font-semibold mb-4 text-slate-200 break-words">Built Within the Use What You Got Ecosystem</h2>
              <p className="text-xs sm:text-base text-slate-400 leading-relaxed max-w-3xl mx-auto break-words">
                NewAudia is not an isolated product. It is part of a broader ecosystem of platforms developed by Use What You Got Publishing—each designed to support creators, businesses, and readers through ethical, practical, and human-centered uses of technology.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
              {/* UWYG Publishing */}
              <a 
                href="https://www.UWYGPublishing.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-slate-900/50 border border-slate-700/50 hover:border-slate-600/70 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:bg-slate-900/70"
              >
                <div className="mb-4 sm:mb-5 h-12 sm:h-20 flex items-center">
                  <img src="https://customer-assets.emergentagent.com/job_6e75eedd-5a24-4ac9-8cba-2cea4f742b15/artifacts/4dy8kqr5_UWYG_Publishing_Ghostwriting_Self_Publishing_Services_Header_Logo.jpg" alt="Use What You Got Publishing" className="max-h-full max-w-full object-contain" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-slate-200 group-hover:text-white transition-colors mb-2 break-words">Use What You Got Publishing</h3>
                <p className="text-xs text-slate-400 leading-relaxed break-words">
                  A modern publishing house built for independent thinkers, focused on long-term value, intellectual ownership, and author stewardship.
                </p>
              </a>
              {/* Digital Ink AI */}
              <a 
                href="https://www.DigitalInkAI.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-slate-900/50 border border-slate-700/50 hover:border-slate-600/70 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:bg-slate-900/70"
              >
                <div className="mb-4 sm:mb-5 h-12 sm:h-20 flex items-center">
                  <img src="https://customer-assets.emergentagent.com/job_6e75eedd-5a24-4ac9-8cba-2cea4f742b15/artifacts/hmgi2dqj_Digital_Ink_AI_Logo_Gold-rbg.png" alt="Digital Ink AI" className="max-h-full max-w-full object-contain" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-slate-200 group-hover:text-white transition-colors mb-2 break-words">Digital Ink AI</h3>
                <p className="text-xs text-slate-400 leading-relaxed break-words">
                  Ethical AI tools for authors and publishers that enhance creative work while preserving originality, authorship, and transparency.
                </p>
              </a>
              {/* AI Means Business */}
              <a 
                href="https://www.AIMeansBusiness.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-slate-900/50 border border-slate-700/50 hover:border-slate-600/70 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all duration-300 hover:bg-slate-900/70"
              >
                <div className="mb-4 sm:mb-5 h-12 sm:h-20 flex items-center">
                  <img src="https://customer-assets.emergentagent.com/job_6e75eedd-5a24-4ac9-8cba-2cea4f742b15/artifacts/ynjzymao_A_I_Means_Business_R_Dale_Brunet.jpg" alt="AI Means Business" className="max-h-full max-w-full object-contain" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-slate-200 group-hover:text-white transition-colors mb-2 break-words">AI Means Business</h3>
                <p className="text-xs text-slate-400 leading-relaxed break-words">
                  Decision-support AI services that help organizations evaluate readiness, opportunity, and responsible adoption of artificial intelligence.
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY NEWAUDIA EXISTS - FOUNDER MESSAGE */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-slate-950 to-slate-900 border-t border-slate-800/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-purple-400 mb-3">A Personal Message</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold mb-4 text-white break-words">Why NewAudia Exists</h2>
            <p className="text-sm sm:text-lg text-gray-300 mb-6 leading-relaxed break-words">
              For more than a decade, audiobooks have lived inside a closed system where most value flows to gatekeepers, not creators or readers. NewAudia was built to prove there's another way.
            </p>
            <button
              onClick={() => setShowFounderModal(true)}
              className="relative inline-flex items-center gap-3 bg-gradient-to-b from-purple-500 via-purple-600 to-indigo-700 border-t border-purple-400/50 text-white px-5 sm:px-8 py-3 sm:py-4 text-sm sm:text-lg font-semibold rounded-2xl shadow-[0_6px_0_0_#312e81,0_8px_20px_rgba(88,28,135,0.4)] hover:shadow-[0_4px_0_0_#312e81,0_6px_16px_rgba(88,28,135,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#312e81] active:translate-y-[5px] transition-all duration-100 cursor-pointer"
              data-testid="why-newaudia-btn"
            >
              <Play className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="break-words">Hear from R. Dale Brunet</span>
            </button>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-hidden">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-white break-words">Welcome to the Future of Reading.</h2>
            <p className="text-sm sm:text-xl text-gray-300 mb-6 sm:mb-10 leading-relaxed break-words">Stories were never meant to be silent.<br />They were meant to be questioned, explored, and understood.</p>
            <div className="flex flex-col gap-3 sm:gap-4 justify-center">
              <button onClick={handleListenFree} className="relative bg-gradient-to-b from-purple-500 via-purple-600 to-indigo-700 border-t border-purple-400/50 text-white px-4 sm:px-10 py-4 sm:py-6 text-sm sm:text-lg font-semibold rounded-2xl shadow-[0_6px_0_0_#312e81,0_8px_20px_rgba(88,28,135,0.4)] hover:shadow-[0_4px_0_0_#312e81,0_6px_16px_rgba(88,28,135,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#312e81] active:translate-y-[5px] transition-all duration-100 w-full whitespace-normal cursor-pointer">▶ Listen Free Right Now</button>
              <button onClick={openAskAudia} className="relative bg-gradient-to-b from-slate-600 via-slate-700 to-slate-800 border-t border-white/20 text-white px-4 sm:px-8 py-4 sm:py-6 text-xs sm:text-lg font-semibold rounded-2xl w-full whitespace-normal shadow-[0_6px_0_0_#1e293b,0_8px_20px_rgba(30,41,59,0.4)] hover:shadow-[0_4px_0_0_#1e293b,0_6px_16px_rgba(30,41,59,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#1e293b] active:translate-y-[5px] transition-all duration-100 cursor-pointer">💬 Or — Ask Audia what to try first</button>
              <button onClick={handleGetStarted} className="relative bg-gradient-to-b from-green-500 via-green-600 to-emerald-700 border-t border-green-400/50 text-white px-4 sm:px-10 py-4 sm:py-6 text-sm sm:text-lg font-semibold rounded-2xl shadow-[0_6px_0_0_#065f46,0_8px_20px_rgba(6,95,70,0.4)] hover:shadow-[0_4px_0_0_#065f46,0_6px_16px_rgba(6,95,70,0.4)] hover:translate-y-[2px] active:shadow-[0_1px_0_0_#065f46] active:translate-y-[5px] transition-all duration-100 w-full whitespace-normal cursor-pointer">🚀 Start Your Journey Free Now!</button>
            </div>
          </div>
        </div>
      </section>

      <VisitorFooter />
      <VisitorMobileBottomNav />

      {/* ASK AUDIA MODAL */}
      <AskAudiaModal isOpen={isAskAudiaOpen} onClose={closeAskAudia} />

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
      />

      {/* Founder Welcome Modal */}
      <FounderWelcomeModal
        isOpen={showFounderModal}
        onClose={() => setShowFounderModal(false)}
      />

      {/* For Authors Modal */}
      <ForAuthorsModal
        isOpen={showAuthorsModal}
        onClose={() => setShowAuthorsModal(false)}
        onGetStarted={() => navigate('/contact')}
      />

      {/* For Publishers Modal */}
      <ForPublishersModal
        isOpen={showPublishersModal}
        onClose={() => setShowPublishersModal(false)}
        onGetStarted={() => navigate('/contact')}
      />
    </div>
  );
};

export default NewLandingPage;
